import AddIcon from '@mui/icons-material/Add';
import { Box, Fab, TablePagination, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { AxiosError, AxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import LayoutBase from '../../shared/layouts';
import { api } from '../../shared/services/api';
import { IResponseWhiteList } from '../../shared/services/types/response';
import { maskCPFCNPJ } from '../../shared/utils/mask';
import ModalWhitelist from './components/modalWhitelist';
import UndrawNoData from '../../assets/svg/undraw_no_data.svg';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import { ConfirmationModal } from '../../shared/components/confirmationModal';
import Loading from '../../components/loading';

interface Column {
  id:
    | 'cnpj_cpf_client'
    | 'name_client'
    | 'account'
    | 'digit'
    | 'agency'
    | 'actions';
  label: string;
  minWidth?: number;
  align?: 'right';
  format?: (value: number) => string;
}

const columns: readonly Column[] = [
  { id: 'name_client', label: 'Nome', minWidth: 170 },
  { id: 'cnpj_cpf_client', label: 'Documento', minWidth: 100 },
  { id: 'agency', label: 'Agencia', minWidth: 170 },
  { id: 'account', label: 'Conta', minWidth: 170 },
  { id: 'digit', label: 'Dígito', minWidth: 170 },
  { id: 'actions', label: 'Ações', minWidth: 100, align: 'right' },
];

const AjudWhitelist = () => {
  const token = Cookies.get('@token');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [dataRows, setDataRows] = useState<IResponseWhiteList[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isCreatedRegister, setIsCreatedRegister] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [editData, setEditData] = useState<IResponseWhiteList | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  useEffect(() => {
    handleGetAll();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (isCreatedRegister) {
      setIsCreatedRegister(false);
      handleGetAll();
    }
  }, [isCreatedRegister]);

  const handleGetAll = async () => {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-type': 'application/json',
        Accept: 'application/json',
      },
      params: {
        limit: rowsPerPage,
        page: page,
      },
    };

    try {
      setIsLoading(true);
      const response = await api.get('/whitelist/getAll', config);

      const {
        data,
        meta: { totalItems },
      } = response.data;

      let dataWhiteList: IResponseWhiteList[] = [];
      dataWhiteList = data;

      setDataRows(dataWhiteList || []);
      setTotalItems(totalItems || 1);
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.message || 'Erro de conexão';
        toast.error(errorMessage);
      } else {
        toast.error('Erro inesperado');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItemId) return;

    try {
      const config = {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      };

      await api.delete(`/whitelist/deleteWhitelist/${selectedItemId}`, config);
      toast.success('Registro excluído com sucesso!');
      handleGetAll();
      setOpenDeleteModal(false);
    } catch (err) {
      toast.error('Erro ao excluir registro');
      console.error('Erro na exclusão:', err);
    } finally {
      setSelectedItemId(null);
    }
  };

  const handleEdit = (row: IResponseWhiteList) => {
    setEditData(row);
    // Lógica para edição (pode ser implementada no modal)
    setOpenModal(true);
    // Você pode precisar passar o registro selecionado para o modal
  };

  return (
    <LayoutBase>
      <Typography color="textPrimary" variant="h5">
        AJUD
      </Typography>
      <Typography sx={{ marginBottom: 2 }}>Whitelist</Typography>
      <Fab
        component="label"
        size="medium"
        color="secondary"
        aria-label="add"
        //disabled={isLoading}
        onClick={() => setOpenModal(true)}
      >
        <AddIcon />
      </Fab>

      <Loading isLoading={isLoading} />

      {dataRows.length === 0 && !isLoading ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              component="img"
              src={UndrawNoData}
              alt="Svg em construção"
              sx={{
                maxWidth: '15%',
                height: 'auto',
                marginTop: 10,
              }}
            />
          </Box>
          <Typography textAlign={'center'} sx={{ marginTop: 2 }}>
            Dados não encontrados...
          </Typography>
        </>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', marginTop: 2 }}>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dataRows.map((row) => {
                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                      {columns.map((column) => {
                        if (column.id === 'actions') {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <IconButton
                                aria-label="editar"
                                onClick={() => handleEdit(row)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                aria-label="excluir"
                                onClick={() => {
                                  setSelectedItemId(row.id);
                                  setOpenDeleteModal(true);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          );
                        }

                        const value = row[column.id];
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {column.id === 'cnpj_cpf_client'
                              ? maskCPFCNPJ(value)
                              : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[15, 30, 50]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
      <ModalWhitelist
        open={openModal}
        whitelistData={editData}
        clearEditData={() => setEditData(null)}
        closeModal={() => setOpenModal(false)}
        whenCreatedSuccess={() => setIsCreatedRegister(true)}
      />

      <ConfirmationModal
        open={openDeleteModal}
        onClose={() => {
          setOpenDeleteModal(false);
          setSelectedItemId(null);
        }}
        onConfirm={handleDelete}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este registro?"
      />
    </LayoutBase>
  );
};

export default AjudWhitelist;
